(function (globalScope, factory) {
  const api = factory();

  if (typeof module === 'object' && module.exports) module.exports = api;
  if (globalScope) globalScope.CernirCustomStrapState = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  const WRIST_BASED_STYLES = Object.freeze(['nato', 'double-roll', 'fixed-2-side']);
  const FOLDING_STYLES = Object.freeze(['single-folding', 'double-folding']);
  const SPRING_BAR_FREE_STYLES = Object.freeze(['fixed-bars', 'middles-cut']);
  const RALLY_STYLES = Object.freeze(['rally-small-holes', 'rally-large-holes', 'rally-hybrid']);
  const CURVED_END_UNSUPPORTED_STYLES = Object.freeze(['wings-cut', 'middles-cut']);
  const NO_STITCHES_STYLE = 'no-stitches';

  const RAW_STATE_DEFAULTS = Object.freeze({
    customType: 'MTO',
    style: 'classic',
    curvedEnd: 'none',
    materialCategory: 'regular-leather',
    material: 'pueblo',
    materialColour: 'dark-brown',
    liningMaterial: 'zermatt-leather',
    liningColour: 'golden',
    keeperColourMode: 'match-main',
    keeper1Colour: 'match-main',
    keeper2Colour: 'match-main',
    lugWidth: null,
    customLugWidthMm: null,
    buckleWidth: null,
    customBuckleWidthMm: null,
    lengthPreset: null,
    customLength: null,
    wristLengthMm: null,
    watchLugToLugMm: null,
    wingsCutDimensions: null,
    middlesCutDimensions: null,
    padding: 'flat',
    thickness: '2.5-1.8',
    foldingThickness: null,
    customThickness: null,
    stitchingStyle: 'classic-stitches',
    threadColour: 'cream',
    tipShape: 'regular-pointed',
    edgeFinishing: 'painted-edges',
    edgeColour: 'on-tone',
    springBars: 'quick-release-spring-bars',
    buckle: 'classic-silver',
    buckleUpload: Object.freeze([]),
    relatedImages: Object.freeze([]),
    personalisation: null,
    noCernirLogo: false,
    notes: null
  });

  function cloneValue(value) {
    if (Array.isArray(value)) return value.slice();
    if (value && typeof value === 'object') return { ...value };
    return value;
  }

  function isThreadColourActive(stitchingStyle, stitchingCatalog) {
    return stitchingCatalog?.isThreadColourActive
      ? stitchingCatalog.isThreadColourActive(stitchingStyle)
      : stitchingStyle !== NO_STITCHES_STYLE;
  }

  function createRawState(overrides) {
    const rawState = {};

    Object.keys(RAW_STATE_DEFAULTS).forEach((key) => {
      const hasOverride = overrides && Object.prototype.hasOwnProperty.call(overrides, key);
      rawState[key] = cloneValue(hasOverride ? overrides[key] : RAW_STATE_DEFAULTS[key]);
    });

    if (rawState.style !== 'wings-cut') rawState.wingsCutDimensions = null;
    if (rawState.style !== 'middles-cut') rawState.middlesCutDimensions = null;
    if (CURVED_END_UNSUPPORTED_STYLES.includes(rawState.style)) rawState.curvedEnd = 'none';
    return rawState;
  }

  function updateRawState(rawState, field, value) {
    if (!Object.prototype.hasOwnProperty.call(RAW_STATE_DEFAULTS, field)) {
      throw new Error(`Unknown rawState field: ${field}`);
    }

    let nextState = { ...rawState, [field]: cloneValue(value) };
    if (field === 'style' && value !== rawState.style) {
      nextState = { ...nextState, wingsCutDimensions: null, middlesCutDimensions: null };
    }
    if (CURVED_END_UNSUPPORTED_STYLES.includes(nextState.style)) nextState.curvedEnd = 'none';
    return nextState;
  }

  function getLengthRoute(style) {
    if (WRIST_BASED_STYLES.includes(style)) return 'wristBased';
    if (style === 'single-folding') return 'singleFolding';
    if (style === 'double-folding') return 'doubleFolding';
    return 'standard';
  }

  function getThicknessRoute(style) {
    if (style === 'nato') return 'none';
    if (FOLDING_STYLES.includes(style)) return 'folding';
    return 'standard';
  }

  function toEffectiveState(rawState, materialCatalog, liningCatalog, dimensionsCatalog, paddingThicknessCatalog, stitchingCatalog, finishingHardwareCatalog, buckleCatalog, additionalCustomisationCatalog, styleCatalog) {
    const effectiveState = createRawState(rawState);
    delete effectiveState.customType;
    const style = rawState.style;
    const lengthRoute = getLengthRoute(style);
    const thicknessRoute = getThicknessRoute(style);

    if (styleCatalog && !styleCatalog.getOption('style', rawState.style)) delete effectiveState.style;
    if (styleCatalog && !styleCatalog.getOption('curvedEnd', rawState.curvedEnd)) delete effectiveState.curvedEnd;
    if (CURVED_END_UNSUPPORTED_STYLES.includes(style)) effectiveState.curvedEnd = 'none';

    const materialIsActive = rawState.material && (!materialCatalog || materialCatalog.isMaterialInCategory(rawState.material, rawState.materialCategory));
    const materialColourIsActive = materialIsActive && rawState.materialColour && (!materialCatalog || materialCatalog.isColourForMaterial(rawState.materialColour, rawState.material));
    if (!materialIsActive) {
      delete effectiveState.material;
      delete effectiveState.materialColour;
    } else if (!materialColourIsActive) {
      delete effectiveState.materialColour;
    }
    const liningMaterialIsActive = rawState.liningMaterial && (!liningCatalog || liningCatalog.isMaterial(rawState.liningMaterial));
    const liningColourIsActive = liningMaterialIsActive && rawState.liningColour && (!liningCatalog || liningCatalog.isColourForMaterial(rawState.liningColour, rawState.liningMaterial));
    if (!liningMaterialIsActive) {
      delete effectiveState.liningMaterial;
      delete effectiveState.liningColour;
    } else if (!liningColourIsActive) {
      delete effectiveState.liningColour;
    }
    const keeperModeIsActive = ['match-main', 'custom'].includes(rawState.keeperColourMode);
    if (!materialColourIsActive || !keeperModeIsActive) {
      delete effectiveState.keeperColourMode;
      delete effectiveState.keeper1Colour;
      delete effectiveState.keeper2Colour;
    } else if (rawState.keeperColourMode === 'match-main') {
      effectiveState.keeper1Colour = 'match-main';
      effectiveState.keeper2Colour = 'match-main';
    } else {
      ['keeper1Colour', 'keeper2Colour'].forEach((field) => {
        const choice = rawState[field];
        if (choice !== 'match-main' && !materialCatalog?.isColourForMaterial(choice, rawState.material)) delete effectiveState[field];
      });
    }
    if (rawState.lugWidth !== 'other') delete effectiveState.customLugWidthMm;
    if (rawState.buckleWidth !== 'other') delete effectiveState.customBuckleWidthMm;

    if (dimensionsCatalog) {
      const lugOption = dimensionsCatalog.getWidthOption('lug', rawState.lugWidth);
      const buckleOption = dimensionsCatalog.getWidthOption('buckle', rawState.buckleWidth);
      const lugWidthMm = dimensionsCatalog.resolveWidthMm(rawState, 'lug');
      const buckleWidthMm = dimensionsCatalog.resolveWidthMm(rawState, 'buckle');
      if (!lugOption) {
        delete effectiveState.lugWidth;
        delete effectiveState.customLugWidthMm;
      } else if (rawState.lugWidth === 'other' && !lugWidthMm) {
        delete effectiveState.customLugWidthMm;
      }
      if (!buckleOption || (lugWidthMm && buckleWidthMm && buckleWidthMm > lugWidthMm)) {
        delete effectiveState.buckleWidth;
        delete effectiveState.customBuckleWidthMm;
      } else if (rawState.buckleWidth === 'other' && !buckleWidthMm) {
        delete effectiveState.customBuckleWidthMm;
      }
    }

    if (lengthRoute === 'wristBased') {
      delete effectiveState.lengthPreset;
      delete effectiveState.customLength;
      if (dimensionsCatalog) {
        if (!dimensionsCatalog.resolvePositiveField(rawState, 'wristLengthMm')) delete effectiveState.wristLengthMm;
        if (!dimensionsCatalog.resolvePositiveField(rawState, 'watchLugToLugMm')) delete effectiveState.watchLugToLugMm;
      }
    } else {
      delete effectiveState.wristLengthMm;
      delete effectiveState.watchLugToLugMm;
      if (rawState.lengthPreset !== 'other') delete effectiveState.customLength;
      if (dimensionsCatalog) {
        const lengthOption = dimensionsCatalog.getLengthOption(style, rawState.lengthPreset);
        if (!lengthOption) {
          delete effectiveState.lengthPreset;
          delete effectiveState.customLength;
        } else if (lengthOption.id === 'other' && !dimensionsCatalog.resolveLength(rawState)) {
          delete effectiveState.customLength;
        }
      }
    }

    if (style !== 'wings-cut' || (dimensionsCatalog && !dimensionsCatalog.resolveStyleMeasurement(rawState, 'wingsCutDimensions'))) {
      delete effectiveState.wingsCutDimensions;
    }
    if (style !== 'middles-cut' || (dimensionsCatalog && !dimensionsCatalog.resolveStyleMeasurement(rawState, 'middlesCutDimensions'))) {
      delete effectiveState.middlesCutDimensions;
    }

    const paddingThicknessState = paddingThicknessCatalog?.normalizeState
      ? paddingThicknessCatalog.normalizeState(rawState)
      : rawState;
    if (paddingThicknessCatalog && !paddingThicknessCatalog.getPaddingOption(paddingThicknessState.padding)) {
      delete effectiveState.padding;
    } else if (paddingThicknessCatalog) {
      effectiveState.padding = paddingThicknessState.padding;
    }

    if (thicknessRoute !== 'standard') {
      delete effectiveState.thickness;
      delete effectiveState.customThickness;
    } else {
      delete effectiveState.foldingThickness;
      effectiveState.thickness = paddingThicknessState.thickness;
      if (paddingThicknessState.thickness !== 'custom') delete effectiveState.customThickness;
      if (paddingThicknessCatalog) {
        const option = paddingThicknessCatalog.getThicknessOption('standard', paddingThicknessState.thickness);
        if (!option) {
          delete effectiveState.thickness;
          delete effectiveState.customThickness;
        } else if (option.id === 'custom' && !paddingThicknessCatalog.resolveCustomThickness(paddingThicknessState)) {
          delete effectiveState.customThickness;
        }
      }
    }

    if (thicknessRoute !== 'folding') delete effectiveState.foldingThickness;
    else if (paddingThicknessCatalog && !paddingThicknessCatalog.getThicknessOption('folding', rawState.foldingThickness)) {
      delete effectiveState.foldingThickness;
    }
    if (stitchingCatalog && !stitchingCatalog.getOption('stitchingStyle', rawState.stitchingStyle)) {
      delete effectiveState.stitchingStyle;
      delete effectiveState.threadColour;
    } else if (!isThreadColourActive(rawState.stitchingStyle, stitchingCatalog)) {
      delete effectiveState.threadColour;
    } else if (stitchingCatalog && !stitchingCatalog.getOption('threadColour', rawState.threadColour)) {
      delete effectiveState.threadColour;
    }
    if (finishingHardwareCatalog) {
      const tipShape = finishingHardwareCatalog.resolveTipShape(rawState);
      const finishingState = finishingHardwareCatalog.normalizeState
        ? finishingHardwareCatalog.normalizeState({ ...rawState, material: effectiveState.material })
        : rawState;
      if (!finishingHardwareCatalog.isTipShapeActive(style)) delete effectiveState.tipShape;
      else if (!finishingHardwareCatalog.getOption('tipShape', rawState.tipShape)) delete effectiveState.tipShape;
      effectiveState.edgeFinishing = finishingState.edgeFinishing;
      if (!finishingHardwareCatalog.isEdgeFinishingAllowed(finishingState.edgeFinishing, tipShape, effectiveState.material)) {
        delete effectiveState.edgeFinishing;
        delete effectiveState.edgeColour;
      } else if (!finishingHardwareCatalog.isEdgeColourActive(finishingState.edgeFinishing)) {
        delete effectiveState.edgeColour;
      } else if (!finishingHardwareCatalog.getOption('edgeColour', rawState.edgeColour)) {
        delete effectiveState.edgeColour;
      }
      if (!finishingHardwareCatalog.isSpringBarsActive(style)
        || !finishingHardwareCatalog.getOption('springBars', rawState.springBars)) delete effectiveState.springBars;
    } else {
      if (style === 'pilot') delete effectiveState.tipShape;
      if (rawState.edgeFinishing !== 'painted-edges') delete effectiveState.edgeColour;
      if (SPRING_BAR_FREE_STYLES.includes(style)) delete effectiveState.springBars;
    }
    if (buckleCatalog) {
      if (!buckleCatalog.isCatalogueActive(rawState) || !buckleCatalog.getOption(rawState.buckle)) delete effectiveState.buckle;
      if (!buckleCatalog.isUploadActive(rawState) || buckleCatalog.validateFiles(rawState.buckleUpload)) delete effectiveState.buckleUpload;
    } else {
      if (rawState.buckleWidth === 'other') delete effectiveState.buckle;
      const buckleUploadActive = rawState.buckleWidth === 'other' || rawState.buckle === 'none';
      if (!buckleUploadActive) delete effectiveState.buckleUpload;
    }
    if (additionalCustomisationCatalog) {
      const relatedImages = Array.from(rawState.relatedImages || []);
      if (!relatedImages.length || additionalCustomisationCatalog.validateRelatedImages(relatedImages)) delete effectiveState.relatedImages;
      // The order gets the text exactly as it will be stamped (no accents), the same as the preview shows.
      const typedPersonalisation = typeof rawState.personalisation === 'string' ? rawState.personalisation : '';
      const personalisation = (additionalCustomisationCatalog.normalisePersonalisation?.(typedPersonalisation) ?? typedPersonalisation).trim();
      if (!personalisation
        || !additionalCustomisationCatalog.isPersonalisationActive(style)
        || additionalCustomisationCatalog.validatePersonalisation(personalisation, style)) delete effectiveState.personalisation;
      else effectiveState.personalisation = personalisation;
      if (typeof rawState.noCernirLogo !== 'boolean') delete effectiveState.noCernirLogo;
      const notes = typeof rawState.notes === 'string' ? rawState.notes.trim() : '';
      if (!notes) delete effectiveState.notes;
      else effectiveState.notes = notes.slice(0, 1000);
    } else if (RALLY_STYLES.includes(style)) {
      delete effectiveState.personalisation;
    }

    return effectiveState;
  }

  function validateCore(rawState, materialCatalog, styleCatalog) {
    const errors = {};
    if (styleCatalog && !styleCatalog.getOption('style', rawState.style)) errors.style = 'Select a strap style.';
    if (styleCatalog && !styleCatalog.getOption('curvedEnd', rawState.curvedEnd)) errors.curvedEnd = 'Select a curved end option.';
    return errors;
  }

  function toOuterMaterialSummary(effectiveState, materialCatalog) {
    const material = materialCatalog?.getMaterial(effectiveState.material);
    const category = material
      ? materialCatalog.DATA.categories.find((item) => item.id === effectiveState.materialCategory)
      : null;
    const colour = material && effectiveState.materialColour
      ? material.colourInventory.find((item) => item.id === effectiveState.materialColour)
      : null;

    return {
      category: category?.label || '',
      material: material?.label || '',
      colour: colour?.label || '',
      displayValue: [material?.label, colour?.label].filter(Boolean).join(' · ')
    };
  }

  function validateOuterMaterial(rawState, materialCatalog) {
    const errors = {};
    const categoryExists = materialCatalog?.DATA.categories.some((item) => item.id === rawState.materialCategory);
    const materialIsActive = categoryExists && materialCatalog.isMaterialInCategory(rawState.material, rawState.materialCategory);
    const colourIsActive = materialIsActive && materialCatalog.isColourForMaterial(rawState.materialColour, rawState.material);

    if (!categoryExists) errors.materialCategory = 'Select a material category.';
    if (!materialIsActive) errors.material = 'Select a material from the active category.';
    if (!colourIsActive) errors.materialColour = 'Select a colour available for the active material.';
    return errors;
  }

  function toLiningSummary(effectiveState, liningCatalog) {
    const material = liningCatalog?.getMaterial(effectiveState.liningMaterial);
    const colour = material && effectiveState.liningColour
      ? material.colourInventory.find((item) => item.id === effectiveState.liningColour && !item.disabled)
      : null;

    return {
      material: material?.label || '',
      colour: colour?.label || '',
      displayValue: [material?.label, colour?.label].filter(Boolean).join(' · ')
    };
  }

  function validateLining(rawState, liningCatalog) {
    const errors = {};
    const materialIsActive = liningCatalog?.isMaterial(rawState.liningMaterial);
    const colourIsActive = materialIsActive && liningCatalog.isColourForMaterial(rawState.liningColour, rawState.liningMaterial);

    if (!materialIsActive) errors.liningMaterial = 'Select a lining material.';
    if (!colourIsActive) errors.liningColour = 'Select a colour available for the active lining material.';
    return errors;
  }

  function keeperChoiceIsValid(choice, rawState, materialCatalog) {
    return choice === 'match-main' || Boolean(materialCatalog?.isColourForMaterial(choice, rawState.material));
  }

  function validateKeepers(rawState, materialCatalog) {
    const errors = {};
    if (!['match-main', 'custom'].includes(rawState.keeperColourMode)) {
      errors.keeperColourMode = 'Choose how the keeper colours should be made.';
      return errors;
    }
    if (rawState.keeperColourMode !== 'custom') return errors;
    if (!keeperChoiceIsValid(rawState.keeper1Colour, rawState, materialCatalog)) errors.keeper1Colour = 'Select a colour available for the active material.';
    if (!keeperChoiceIsValid(rawState.keeper2Colour, rawState, materialCatalog)) errors.keeper2Colour = 'Select a colour available for the active material.';
    return errors;
  }

  function toKeeperSummary(effectiveState, materialCatalog) {
    const material = materialCatalog?.getMaterial(effectiveState.material);
    const mainColour = material && effectiveState.materialColour
      ? material.colourInventory.find((item) => item.id === effectiveState.materialColour)
      : null;
    const resolve = (choice) => {
      if (!mainColour) return '';
      if (choice === 'match-main') return `Match main leather — ${mainColour.label}`;
      return material?.colourInventory.find((item) => item.id === choice)?.label || '';
    };
    const mode = !mainColour ? '' : (effectiveState.keeperColourMode === 'custom' ? 'Choose keeper colours separately' : (effectiveState.keeperColourMode === 'match-main' ? 'Match main leather' : ''));
    return {
      mode,
      keeper1Colour: resolve(effectiveState.keeper1Colour),
      keeper2Colour: resolve(effectiveState.keeper2Colour),
      displayValue: [mode, resolve(effectiveState.keeper1Colour), resolve(effectiveState.keeper2Colour)].filter(Boolean).join(' · ')
    };
  }

  function validateDimensions(rawState, dimensionsCatalog) {
    const errors = {};
    if (!dimensionsCatalog) return errors;

    const lugOption = dimensionsCatalog.getWidthOption('lug', rawState.lugWidth);
    const buckleOption = dimensionsCatalog.getWidthOption('buckle', rawState.buckleWidth);
    const lugWidthMm = dimensionsCatalog.resolveWidthMm(rawState, 'lug');
    const buckleWidthMm = dimensionsCatalog.resolveWidthMm(rawState, 'buckle');

    if (!lugOption) errors.lugWidth = 'Select a lug width.';
    else if (lugOption.id === 'other' && !lugWidthMm) errors.customLugWidthMm = Number(rawState.customLugWidthMm) > 100 ? 'For lug widths over 100 mm, please contact us.' : 'Enter a lug width greater than 0.';
    if (!buckleOption) errors.buckleWidth = 'Select a buckle width.';
    else if (buckleOption.id === 'other' && !buckleWidthMm) errors.customBuckleWidthMm = Number(rawState.customBuckleWidthMm) > 100 ? 'For buckle widths over 100 mm, please contact us.' : 'Enter a buckle width greater than 0.';

    if (lugWidthMm && buckleWidthMm && buckleWidthMm > lugWidthMm) {
      const field = buckleOption?.id === 'other' ? 'customBuckleWidthMm' : 'buckleWidth';
      errors[field] = 'Buckle width cannot be greater than lug width.';
    }

    if (dimensionsCatalog.getLengthRouteId(rawState.style) === 'wristBased') {
      if (!dimensionsCatalog.resolvePositiveField(rawState, 'wristLengthMm')) {
        errors.wristLengthMm = 'Enter a wrist length greater than 0.';
      }
      if (!dimensionsCatalog.resolvePositiveField(rawState, 'watchLugToLugMm')) {
        errors.watchLugToLugMm = 'Enter a watch lug-to-lug measurement greater than 0.';
      }
    } else {
      const lengthOption = dimensionsCatalog.getLengthOption(rawState.style, rawState.lengthPreset);
      if (!lengthOption) {
        errors.lengthPreset = 'Select a strap length.';
      } else if (lengthOption.id === 'other') {
        if (!dimensionsCatalog.toPositiveNumber(rawState.customLength?.longEndMm, 'customLengthLongEnd')) {
          errors.customLengthLongEnd = 'Enter a long end greater than 0.';
        }
        if (!dimensionsCatalog.toPositiveNumber(rawState.customLength?.shortEndMm, 'customLengthShortEnd')) {
          errors.customLengthShortEnd = 'Enter a short end greater than 0.';
        }
      }
    }

    for (const field of ['wingsCutDimensions', 'middlesCutDimensions']) {
      const definition = dimensionsCatalog.getStyleMeasurement(field);
      if (!definition || rawState.style !== definition.style) continue;
      for (const component of definition.components) {
        const value = dimensionsCatalog.toPositiveNumber(rawState[field]?.[component.id], 'styleMeasurement');
        const errorField = `${field}${component.id[0].toUpperCase()}${component.id.slice(1)}`;
        if (!value) {
          errors[errorField] = `Enter ${component.label} greater than 0.`;
        } else if (component.mustBeLessThan === 'lugWidth' && lugWidthMm && value >= lugWidthMm) {
          errors[errorField] = `${component.label} must be smaller than Lug Width.`;
        }
      }
    }
    return errors;
  }

  function validatePaddingThickness(rawState, paddingThicknessCatalog) {
    const errors = {};
    if (!paddingThicknessCatalog) return errors;
    if (!paddingThicknessCatalog.getPaddingOption(rawState.padding)) errors.padding = 'Select a padding profile.';

    const route = paddingThicknessCatalog.getRoute(rawState.style);
    if (route === 'standard') {
      const option = paddingThicknessCatalog.getThicknessOption('standard', rawState.thickness);
      if (!option) errors.thickness = 'Select a standard thickness.';
      else if (!paddingThicknessCatalog.isThicknessSupported(rawState.padding, option.id)) {
        errors.thickness = 'This thickness is not supported with the selected Padded style.';
      }
      else if (option.id === 'custom') {
        if (!paddingThicknessCatalog.toPositiveNumber(rawState.customThickness?.lugMm)) {
          errors.customThicknessLug = 'Enter a lug-end thickness greater than 0.';
        }
        if (!paddingThicknessCatalog.toPositiveNumber(rawState.customThickness?.tipMm)) {
          errors.customThicknessTip = 'Enter a tip-end thickness greater than 0.';
        } else if (paddingThicknessCatalog.toPositiveNumber(rawState.customThickness?.lugMm)
          && Number(rawState.customThickness.tipMm) > Number(rawState.customThickness.lugMm)) {
          errors.customThicknessTip = 'The strap-end thickness can\'t be more than the watch-end thickness.';
        } else if ((paddingThicknessCatalog.getPaddingRule(rawState.padding)?.unsupportedThicknesses || []).some((id) => {
          const [lugMm, tipMm] = id.split('-').map(Number);
          return lugMm === Number(rawState.customThickness?.lugMm) && tipMm === Number(rawState.customThickness?.tipMm);
        })) {
          errors.customThicknessTip = 'This thickness is not available with the selected padding.';
        }
      }
    } else if (route === 'folding' && !paddingThicknessCatalog.getThicknessOption('folding', rawState.foldingThickness)) {
      errors.foldingThickness = 'Select a folding thickness.';
    }
    return errors;
  }

  function toPaddingThicknessSummary(effectiveState, paddingThicknessCatalog) {
    if (!paddingThicknessCatalog) return { padding: '', thickness: '', foldingThickness: '', displayValue: '' };
    const padding = paddingThicknessCatalog.getPaddingOption(effectiveState.padding)?.label.replace(/\s*\(Default\)\s*$/, '') || '';
    const route = paddingThicknessCatalog.getRoute(effectiveState.style);
    let thickness = '';
    let foldingThickness = '';
    if (route === 'standard') {
      const option = paddingThicknessCatalog.getThicknessOption('standard', effectiveState.thickness);
      if (option?.id === 'custom') thickness = paddingThicknessCatalog.formatPair(paddingThicknessCatalog.resolveCustomThickness(effectiveState));
      else thickness = option?.label || '';
    }
    if (route === 'folding') {
      foldingThickness = paddingThicknessCatalog.getThicknessOption('folding', effectiveState.foldingThickness)?.label || '';
    }
    return {
      padding,
      thickness,
      foldingThickness,
      displayValue: [padding, thickness || foldingThickness].filter(Boolean).join(' · ')
    };
  }

  function validateStitching(rawState, stitchingCatalog) {
    const errors = {};
    if (!stitchingCatalog) return errors;
    if (!stitchingCatalog.getOption('stitchingStyle', rawState.stitchingStyle)) {
      errors.stitchingStyle = 'Select a stitching style.';
    } else if (stitchingCatalog.isThreadColourActive(rawState.stitchingStyle)
      && !stitchingCatalog.getOption('threadColour', rawState.threadColour)) {
      errors.threadColour = 'Select a stitch colour.';
    }
    return errors;
  }

  function toStitchingSummary(effectiveState, stitchingCatalog) {
    if (!stitchingCatalog) return { stitchingStyle: '', threadColour: '', displayValue: '' };
    const stitchingStyle = stitchingCatalog.getOption('stitchingStyle', effectiveState.stitchingStyle)?.title || '';
    const threadColour = stitchingCatalog.getOption('threadColour', effectiveState.threadColour)?.title || '';
    return {
      stitchingStyle,
      threadColour,
      displayValue: [stitchingStyle, threadColour].filter(Boolean).join(' · ')
    };
  }

  function validateFinishingHardware(rawState, finishingHardwareCatalog) {
    const errors = {};
    if (!finishingHardwareCatalog) return errors;
    const tipShape = finishingHardwareCatalog.resolveTipShape(rawState);
    if (finishingHardwareCatalog.isTipShapeActive(rawState.style)
      && !finishingHardwareCatalog.getOption('tipShape', rawState.tipShape)) errors.tipShape = 'Select a tip shape.';
    if (!finishingHardwareCatalog.isEdgeFinishingAllowed(rawState.edgeFinishing, tipShape, rawState.material)) {
      errors.edgeFinishing = tipShape === 'pilot' && rawState.edgeFinishing === 'folded-edges'
        ? 'Pilot tips require Painted Edges.'
        : rawState.edgeFinishing === 'folded-edges'
          ? 'Folded Edges are unsupported for the selected leather.'
          : 'Select an edge finish.';
    } else if (finishingHardwareCatalog.isEdgeColourActive(rawState.edgeFinishing)
      && !finishingHardwareCatalog.getOption('edgeColour', rawState.edgeColour)) {
      errors.edgeColour = 'Select a painted edge colour.';
    }
    if (finishingHardwareCatalog.isSpringBarsActive(rawState.style)
      && !finishingHardwareCatalog.getOption('springBars', rawState.springBars)) errors.springBars = 'Select spring bars.';
    return errors;
  }

  function toFinishingHardwareSummary(effectiveState, rawState, finishingHardwareCatalog) {
    if (!finishingHardwareCatalog) return { tipShape: '', edgeFinishing: '', edgeColour: '', springBars: '', displayValue: '' };
    const resolvedTipShape = effectiveState.style === 'pilot' ? 'pilot' : effectiveState.tipShape;
    const tipShape = finishingHardwareCatalog.getOption('tipShape', resolvedTipShape)?.title || '';
    const edgeFinishing = finishingHardwareCatalog.getOption('edgeFinishing', effectiveState.edgeFinishing)?.title || '';
    const edgeColour = finishingHardwareCatalog.getOption('edgeColour', effectiveState.edgeColour)?.title || '';
    const springBars = finishingHardwareCatalog.getOption('springBars', effectiveState.springBars)?.title || '';
    return {
      tipShape,
      edgeFinishing,
      edgeColour,
      springBars,
      displayValue: [tipShape, edgeFinishing, edgeColour, springBars].filter(Boolean).join(' · ')
    };
  }

  function validateBuckle(rawState, buckleCatalog) {
    const errors = {};
    if (!buckleCatalog) return errors;
    if (buckleCatalog.isCatalogueActive(rawState) && !buckleCatalog.getOption(rawState.buckle)) {
      errors.buckle = 'Select a buckle.';
    }
    if (buckleCatalog.isUploadActive(rawState)) {
      const uploadError = buckleCatalog.validateFiles(rawState.buckleUpload);
      if (uploadError) errors.buckleUpload = uploadError;
    }
    return errors;
  }

  function toBuckleSummary(effectiveState, buckleCatalog) {
    if (!buckleCatalog) return { buckle: '', buckleUpload: '', displayValue: '' };
    const buckle = buckleCatalog.getOption(effectiveState.buckle)?.title || '';
    const buckleUpload = Array.from(effectiveState.buckleUpload || []).map((file) => file.name).filter(Boolean).join(', ');
    return {
      buckle,
      buckleUpload,
      displayValue: [buckle, buckleUpload].filter(Boolean).join(' · ')
    };
  }

  function validateAdditionalCustomisation(rawState, additionalCustomisationCatalog) {
    const errors = {};
    if (!additionalCustomisationCatalog) return errors;
    const relatedImagesError = additionalCustomisationCatalog.validateRelatedImages(rawState.relatedImages);
    const personalisationError = additionalCustomisationCatalog.validatePersonalisation(rawState.personalisation, rawState.style);
    if (relatedImagesError) errors.relatedImages = relatedImagesError;
    if (personalisationError) errors.personalisation = personalisationError;
    return errors;
  }

  function toAdditionalCustomisationSummary(effectiveState) {
    const relatedImages = Array.from(effectiveState.relatedImages || []).map((file) => file.name).filter(Boolean).join(', ');
    const personalisation = typeof effectiveState.personalisation === 'string' ? effectiveState.personalisation.trim() : '';
    const noCernirLogo = effectiveState.noCernirLogo === true ? 'Yes' : '';
    const notes = typeof effectiveState.notes === 'string' ? effectiveState.notes.trim() : '';
    return {
      relatedImages,
      personalisation,
      noCernirLogo,
      notes,
      displayValue: [relatedImages, personalisation, noCernirLogo ? 'No CERNIR Logo' : '', notes].filter(Boolean).join(' · ')
    };
  }

  function toDimensionsSummary(effectiveState, dimensionsCatalog) {
    if (!dimensionsCatalog) return { lugWidth: '', buckleWidth: '', strapLength: '', wristLength: '', watchLugToLug: '', centerWidth: '', cutWidth: '', cutDepth: '', displayValue: '' };
    const lugWidthMm = dimensionsCatalog.resolveWidthMm(effectiveState, 'lug');
    const buckleWidthMm = dimensionsCatalog.resolveWidthMm(effectiveState, 'buckle');
    const length = dimensionsCatalog.resolveLength(effectiveState);
    const wristLengthMm = dimensionsCatalog.resolvePositiveField(effectiveState, 'wristLengthMm');
    const watchLugToLugMm = dimensionsCatalog.resolvePositiveField(effectiveState, 'watchLugToLugMm');
    const wings = dimensionsCatalog.resolveStyleMeasurement(effectiveState, 'wingsCutDimensions');
    const middles = dimensionsCatalog.resolveStyleMeasurement(effectiveState, 'middlesCutDimensions');
    const centerWidth = wings?.centerWidth ? `${wings.centerWidth} mm` : '';
    const cutWidth = middles?.cutWidth ? `${middles.cutWidth} mm` : '';
    const cutDepthValue = wings?.cutDepth || middles?.cutDepth;
    const cutDepth = cutDepthValue ? `${cutDepthValue} mm` : '';
    const summary = {
      lugWidth: lugWidthMm ? `${lugWidthMm} mm` : '',
      buckleWidth: buckleWidthMm ? `${buckleWidthMm} mm` : '',
      strapLength: length ? `${length.longEndMm}/${length.shortEndMm} mm` : '',
      wristLength: wristLengthMm ? `${wristLengthMm} mm` : '',
      watchLugToLug: watchLugToLugMm ? `${watchLugToLugMm} mm` : '',
      centerWidth,
      cutWidth,
      cutDepth
    };
    return { ...summary, displayValue: Object.values(summary).filter(Boolean).join(' · ') };
  }

  function toDerivedState(rawState, effectiveState, materialCatalog, liningCatalog, dimensionsCatalog, paddingThicknessCatalog, stitchingCatalog, finishingHardwareCatalog, buckleCatalog, additionalCustomisationCatalog, styleCatalog) {
    const effective = effectiveState || toEffectiveState(rawState, materialCatalog, liningCatalog, dimensionsCatalog, paddingThicknessCatalog, stitchingCatalog, finishingHardwareCatalog, buckleCatalog, additionalCustomisationCatalog, styleCatalog);
    const effectiveTipShape = effective.style === 'pilot' ? 'pilot' : effective.tipShape;
    const lengthRoute = getLengthRoute(effective.style);
    const thicknessRoute = getThicknessRoute(effective.style);
    return {
      lengthRoute,
      thicknessRoute,
      effectiveTipShape,
      usesCustomLugWidth: rawState.lugWidth === 'other',
      usesCustomBuckleWidth: rawState.buckleWidth === 'other',
      usesCustomLength: lengthRoute !== 'wristBased' && rawState.lengthPreset === 'other',
      usesCustomThickness: thicknessRoute === 'standard' && rawState.thickness === 'custom',
      requiresBuckleUpload: rawState.buckleWidth === 'other' || rawState.buckle === 'none',
      foldedPilotConflict: effectiveTipShape === 'pilot' && rawState.edgeFinishing === 'folded-edges',
      outerMaterialSummary: toOuterMaterialSummary(effective, materialCatalog),
      liningSummary: toLiningSummary(effective, liningCatalog),
      keeperSummary: toKeeperSummary(effective, materialCatalog),
      dimensionsSummary: toDimensionsSummary(effective, dimensionsCatalog),
      paddingThicknessSummary: toPaddingThicknessSummary(effective, paddingThicknessCatalog),
      stitchingSummary: toStitchingSummary(effective, stitchingCatalog),
      finishingHardwareSummary: toFinishingHardwareSummary(effective, rawState, finishingHardwareCatalog),
      buckleSummary: toBuckleSummary(effective, buckleCatalog),
      personalisationActive: additionalCustomisationCatalog
        ? additionalCustomisationCatalog.isPersonalisationActive(rawState.style)
        : !RALLY_STYLES.includes(rawState.style),
      additionalCustomisationSummary: toAdditionalCustomisationSummary(effective),
      configurationSummary: toConfigurationSummary(effective, materialCatalog, liningCatalog, dimensionsCatalog, paddingThicknessCatalog, stitchingCatalog, finishingHardwareCatalog, buckleCatalog, additionalCustomisationCatalog, styleCatalog)
    };
  }

  function toCartProperties(effectiveState, materialCatalog, liningCatalog, dimensionsCatalog, paddingThicknessCatalog, stitchingCatalog, finishingHardwareCatalog, rawState, buckleCatalog, additionalCustomisationCatalog, styleCatalog) {
    const properties = {};
    const style = styleCatalog?.getOption('style', effectiveState.style);
    const curvedEnd = styleCatalog?.getOption('curvedEnd', effectiveState.curvedEnd);
    if (style) properties['Strap Style'] = style.title;
    if (curvedEnd) properties['Curved End'] = curvedEnd.title;
    const materialSummary = toOuterMaterialSummary(effectiveState, materialCatalog);
    if (materialSummary.material) {
      if (materialSummary.category) properties['Material Category'] = materialSummary.category;
      properties['Outer Material'] = materialSummary.material;
      if (materialSummary.colour) properties['Material Colour'] = materialSummary.colour;
    }
    const liningSummary = toLiningSummary(effectiveState, liningCatalog);
    if (liningSummary.material) {
      properties['Lining Material'] = liningSummary.material;
      if (liningSummary.colour) properties['Lining Colour'] = liningSummary.colour;
    }
    const keeperSummary = toKeeperSummary(effectiveState, materialCatalog);
    if (keeperSummary.mode) properties['Keeper Colour Mode'] = keeperSummary.mode;
    if (keeperSummary.keeper1Colour) properties['Keeper 1 Colour'] = keeperSummary.keeper1Colour;
    if (keeperSummary.keeper2Colour) properties['Keeper 2 Colour'] = keeperSummary.keeper2Colour;
    const dimensionsSummary = toDimensionsSummary(effectiveState, dimensionsCatalog);
    if (dimensionsSummary.lugWidth) properties['Lug Width'] = dimensionsSummary.lugWidth;
    if (dimensionsSummary.buckleWidth) properties['Buckle Width'] = dimensionsSummary.buckleWidth;
    if (dimensionsSummary.strapLength) properties['Strap Length'] = dimensionsSummary.strapLength;
    if (dimensionsSummary.wristLength) properties['Wrist Length'] = dimensionsSummary.wristLength;
    if (dimensionsSummary.watchLugToLug) properties['Watch Lug-to-Lug'] = dimensionsSummary.watchLugToLug;
    if (dimensionsSummary.centerWidth) properties['Center Width'] = dimensionsSummary.centerWidth;
    if (dimensionsSummary.cutWidth) properties['Cut Width'] = dimensionsSummary.cutWidth;
    if (dimensionsSummary.cutDepth) properties['Cut Depth'] = dimensionsSummary.cutDepth;
    const paddingThicknessSummary = toPaddingThicknessSummary(effectiveState, paddingThicknessCatalog);
    if (paddingThicknessSummary.padding) properties.Padding = paddingThicknessSummary.padding;
    if (paddingThicknessSummary.thickness) properties.Thickness = paddingThicknessSummary.thickness;
    if (paddingThicknessSummary.foldingThickness) properties['Folding Thickness'] = paddingThicknessSummary.foldingThickness;
    const stitchingSummary = toStitchingSummary(effectiveState, stitchingCatalog);
    if (stitchingSummary.stitchingStyle) properties.Stitching = stitchingSummary.stitchingStyle;
    if (stitchingSummary.threadColour) properties['Stitch Colour'] = stitchingSummary.threadColour;
    const finishingSummary = toFinishingHardwareSummary(effectiveState, rawState || effectiveState, finishingHardwareCatalog);
    if (finishingSummary.tipShape) properties['Tip Shape'] = finishingSummary.tipShape;
    if (finishingSummary.edgeFinishing) properties['Edge Finishing'] = finishingSummary.edgeFinishing;
    if (finishingSummary.edgeColour) properties['Painted Edge Colour'] = finishingSummary.edgeColour;
    if (finishingSummary.springBars) properties['Spring Bars'] = finishingSummary.springBars;
    const buckleSummary = toBuckleSummary(effectiveState, buckleCatalog);
    if (buckleSummary.buckle) properties.Buckle = buckleSummary.buckle;
    if (buckleSummary.buckleUpload) properties['Buckle Image Upload'] = buckleSummary.buckleUpload;
    const additionalSummary = toAdditionalCustomisationSummary(effectiveState, additionalCustomisationCatalog);
    if (additionalSummary.relatedImages) properties['Related Image Uploads'] = additionalSummary.relatedImages;
    if (additionalSummary.personalisation) properties.Personalisation = additionalSummary.personalisation;
    if (additionalSummary.noCernirLogo) properties['No CERNIR Logo'] = additionalSummary.noCernirLogo;
    if (additionalSummary.notes) properties.Notes = additionalSummary.notes;
    return properties;
  }

  function toConfigurationSummary(effectiveState, materialCatalog, liningCatalog, dimensionsCatalog, paddingThicknessCatalog, stitchingCatalog, finishingHardwareCatalog, buckleCatalog, additionalCustomisationCatalog, styleCatalog) {
    const properties = toCartProperties(effectiveState, materialCatalog, liningCatalog, dimensionsCatalog, paddingThicknessCatalog, stitchingCatalog, finishingHardwareCatalog, effectiveState, buckleCatalog, additionalCustomisationCatalog, styleCatalog);
    return Object.entries(properties).map(([label, value]) => Object.freeze({ label, value }));
  }

  return Object.freeze({
    RAW_STATE_DEFAULTS,
    createRawState,
    updateRawState,
    toEffectiveState,
    toDerivedState,
    toOuterMaterialSummary,
    validateOuterMaterial,
    toLiningSummary,
    validateLining,
    toKeeperSummary,
    validateKeepers,
    toDimensionsSummary,
    validateDimensions,
    toPaddingThicknessSummary,
    validatePaddingThickness,
    toStitchingSummary,
    validateStitching,
    toFinishingHardwareSummary,
    validateFinishingHardware,
    toBuckleSummary,
    validateBuckle,
    toAdditionalCustomisationSummary,
    validateAdditionalCustomisation,
    validateCore,
    toConfigurationSummary,
    toCartProperties
  });
});
